/**
 * Indicator helpers — compute on the frontend over the quote series.
 * Keeps backend free of per-stock chart math; series are short (< 100 points).
 */

export function simpleMovingAverage(values, period) {
  const out = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= period) sum -= values[i - period];
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

export function bollingerBands(values, period = 20, mult = 2) {
  const mid = simpleMovingAverage(values, period);
  const upper = new Array(values.length).fill(null);
  const lower = new Array(values.length).fill(null);
  for (let i = period - 1; i < values.length; i++) {
    const window = values.slice(i - period + 1, i + 1);
    const mean = mid[i];
    if (mean == null) continue;
    const variance = window.reduce((acc, v) => acc + (v - mean) ** 2, 0) / period;
    const std = Math.sqrt(variance);
    upper[i] = mean + mult * std;
    lower[i] = mean - mult * std;
  }
  return { mid, upper, lower };
}

export function rsi(values, period = 14) {
  const out = new Array(values.length).fill(null);
  if (values.length <= period) return out;
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const diff = values[i] - values[i - 1];
    if (diff > 0) avgGain += diff;
    else avgLoss -= diff;
  }
  avgGain /= period;
  avgLoss /= period;
  out[period] = 100 - 100 / (1 + (avgLoss === 0 ? 100 : avgGain / avgLoss));
  for (let i = period + 1; i < values.length; i++) {
    const diff = values[i] - values[i - 1];
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    out[i] = 100 - 100 / (1 + (avgLoss === 0 ? 100 : avgGain / avgLoss));
  }
  return out;
}

function ema(values, period) {
  const out = new Array(values.length).fill(null);
  const k = 2 / (period + 1);
  let prev = null;
  for (let i = 0; i < values.length; i++) {
    if (values[i] == null) continue;
    if (prev == null) {
      prev = values[i];
    } else {
      prev = values[i] * k + prev * (1 - k);
    }
    out[i] = prev;
  }
  return out;
}

export function macd(values, fast = 12, slow = 26, signal = 9) {
  const emaFast = ema(values, fast);
  const emaSlow = ema(values, slow);
  const macdLine = values.map((_, i) =>
    emaFast[i] != null && emaSlow[i] != null ? emaFast[i] - emaSlow[i] : null,
  );
  const signalLine = ema(macdLine.map((v) => (v == null ? 0 : v)), signal);
  const histogram = macdLine.map((v, i) =>
    v != null && signalLine[i] != null ? v - signalLine[i] : null,
  );
  return { macdLine, signalLine, histogram };
}

export function cumulativeReturns(values) {
  if (!values.length) return [];
  const base = values[0];
  return values.map((v) => (v / base - 1) * 100);
}

export function drawdownSeries(values) {
  let peak = values[0] || 0;
  return values.map((v) => {
    if (v > peak) peak = v;
    return peak ? (v / peak - 1) * 100 : 0;
  });
}

export function shortDate(iso) {
  if (!iso) return "";
  if (typeof iso === "string") {
    const parts = iso.split("-");
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2].slice(0, 2)}/${parts[1]}`;
    }
  }
  return String(iso);
}

const MONTH_NAMES_SHORT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

export function monthYearLabel(iso) {
  if (!iso || typeof iso !== "string") return "";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  // Render as MM/AAAA (numeric) for compactness
  return `${parts[1]}/${parts[0]}`;
}

export function fullDateLabel(iso) {
  if (!iso || typeof iso !== "string") return iso ?? "";
  const parts = iso.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2].slice(0, 2)}/${parts[1]}/${parts[0]}`;
  }
  return iso;
}

/**
 * Returns the ISO date of the first data point of each month present in `rows`.
 * Useful as the `ticks` prop for Recharts XAxis so labels stay readable.
 * If too many months exist, samples evenly to keep at most `maxTicks` labels.
 */
export function monthlyTicks(rows, dateKey = "raw_date", maxTicks = 12) {
  if (!rows || !rows.length) return [];
  const seen = new Set();
  const all = [];
  for (const r of rows) {
    const iso = r[dateKey];
    if (!iso || typeof iso !== "string") continue;
    const monthKey = iso.slice(0, 7); // YYYY-MM
    if (!seen.has(monthKey)) {
      seen.add(monthKey);
      all.push(iso);
    }
  }
  if (all.length <= maxTicks) return all;
  const step = Math.ceil(all.length / maxTicks);
  const sampled = [];
  for (let i = 0; i < all.length; i += step) sampled.push(all[i]);
  // Always keep the last month
  if (sampled[sampled.length - 1] !== all[all.length - 1]) sampled.push(all[all.length - 1]);
  return sampled;
}


/**
 * Compute per-candle colour based on the SMA of highs vs. lows of the previous
 * window. Pure, causal, no look-ahead — exposed for unit testing.
 *
 * Rules (for each candle j):
 *   medlow_{j-1} = SMA(low, window=N) at index j-1
 *   medhi_{j-1}  = SMA(high, window=N) at index j-1
 *   med_{j-1}    = (medlow_{j-1} + medhi_{j-1}) / 2
 *   compare med_{j-1} against the price range (low_{j-2}, high_{j-2})
 *     > hi_{j-2}            -> "green"   (uptrend)
 *     < low_{j-2}           -> "red"     (downtrend)
 *     within [low, high]    -> "blue"    (sideways / undefined)
 *
 * Candles with j < max(N, 2) return null (no data).
 *
 * @param {Array<{open:number, high:number, low:number, close:number}>} candles
 * @param {number} N  Moving-average window (>= 1)
 * @param {"sma"} tipoMedia  Only "sma" implemented so far
 * @returns {Array<null | "green" | "blue" | "red">}
 */
export function computeCandleColors(candles, N = 20, tipoMedia = "sma") {
  const out = new Array(candles.length).fill(null);
  if (!candles || candles.length === 0 || N < 1) return out;
  if (tipoMedia !== "sma") {
    throw new Error(`computeCandleColors: tipoMedia '${tipoMedia}' não suportado.`);
  }
  const highs = candles.map((c) => Number(c.high));
  const lows = candles.map((c) => Number(c.low));
  const medHigh = simpleMovingAverage(highs, N);
  const medLow = simpleMovingAverage(lows, N);

  for (let j = 2; j < candles.length; j++) {
    const med_jm1 =
      medHigh[j - 1] == null || medLow[j - 1] == null
        ? null
        : (medHigh[j - 1] + medLow[j - 1]) / 2;
    if (med_jm1 == null) continue;
    const hi_jm2 = Number(candles[j - 2].high);
    const low_jm2 = Number(candles[j - 2].low);
    if (!Number.isFinite(hi_jm2) || !Number.isFinite(low_jm2)) continue;
    if (med_jm1 > hi_jm2) out[j] = "green";
    else if (med_jm1 < low_jm2) out[j] = "red";
    else out[j] = "blue"; // inclusive on both edges
  }
  return out;
}

/**
 * Companion helper that returns the raw `med` series (same length as candles,
 * null for indices where the window isn't satisfied). Useful to overlay the
 * trailing average on the colourised chart.
 */
export function computeCandleMedSeries(candles, N = 20) {
  const highs = candles.map((c) => Number(c.high));
  const lows = candles.map((c) => Number(c.low));
  const medHigh = simpleMovingAverage(highs, N);
  const medLow = simpleMovingAverage(lows, N);
  return medHigh.map((h, i) =>
    h == null || medLow[i] == null ? null : (h + medLow[i]) / 2,
  );
}
